import type { CSSProperties, ReactNode } from 'react';
import styles from './Skeleton.module.css';

export type SkeletonShape = 'text' | 'title' | 'circle' | 'card';

interface SkeletonProps {
  readonly shape?: SkeletonShape;
  /** A CSS width. Measured or proportional geometry, so it is legitimately inline. */
  readonly width?: string;
}

/**
 * SYS-02. Never a full-page spinner, and never for data already on screen — a
 * refetch keeps the previous result and swaps it in place (06-qol §5). A skeleton
 * is only for content that has never been painted, shaped like what will arrive.
 */
export function Skeleton({ shape = 'text', width }: SkeletonProps): ReactNode {
  const style: CSSProperties | undefined = width === undefined ? undefined : { width };
  return <span className={`${styles.skeleton} ${styles[shape]}`} style={style} aria-hidden />;
}

interface SkeletonLinesProps {
  readonly count?: number;
}

/** A paragraph's worth, with a short last line the way real text ends. */
export function SkeletonLines({ count = 3 }: SkeletonLinesProps): ReactNode {
  return (
    <div className={styles.lines} aria-hidden>
      {Array.from({ length: count }, (_, index) => (
        <span key={index} className={`${styles.skeleton} ${styles.text}`} />
      ))}
    </div>
  );
}
